import { useMe, useSetGroupRole } from '@echo/core/react';
import { Button } from '@echo/ui-web';
import { Trans, useLingui } from '@lingui/react/macro';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { ToolbarBack } from '@/components/empty-state-frame';
import { GroupActionFailed } from '@/components/group-load-state';
import { useGoBack } from '@/lib/use-go-back';

export const Route = createFileRoute('/_app/account_/groups_/$id_/facilitator')({
  component: BecomeFacilitatorPage,
});

/**
 * Manage group › Learn more (Figma account/groups, "Become a Facilitator"): what identifying as
 * this group's Facilitator means, what changes and what doesn't, then "Accept - Become
 * Facilitator" or "Not now". Accepting gives the person the group's Facilitator role beside
 * Group Leader (Admin); Manage group then tags them Facilitator.
 */
function BecomeFacilitatorPage() {
  const { t } = useLingui();
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const me = useMe();
  const setRole = useSetGroupRole(id);
  const goBack = useGoBack(
    () => void navigate({ to: '/account/groups/$id', params: { id }, replace: true }),
  );
  const accept = () => {
    const personId = me.data?.uuid;
    if (!personId || setRole.isPending) return;
    setRole.mutate({ personId, role: 'facilitator' }, { onSuccess: goBack });
  };
  return (
    <>
      <ToolbarBack
        render={({ className, children }) => (
          <button type="button" className={className} onClick={goBack}>
            {children}
          </button>
        )}
      />
      <h1 className="text-display-sm font-bold">
        <Trans>Become a Facilitator</Trans>
      </h1>
      <div className="flex flex-col gap-content-md pt-content-md">
        <section className="flex flex-col gap-content-lg rounded-xl border border-border-weak bg-surface p-content-sm">
          <p className="text-body-md text-on-surface-medium">
            <Trans>
              Identifying as a Facilitator means you say you completed all tasks of the
              StrongerMemory Facilitator's Resource Guide and intend to lead this group while not
              also completing exercises as Members do.
            </Trans>
          </p>
          <h2 className="text-title-md font-bold text-on-surface-medium">
            <Trans>What changes:</Trans>
          </h2>
          <ol className="flex flex-col gap-internal-lg">
            <Change number={1}>
              <Trans>You become a Facilitator of this group, instead of Leader.</Trans>
            </Change>
            <Change number={2}>
              <Trans>Other people in this group won't see your exercises.</Trans>
            </Change>
          </ol>
          <h2 className="text-title-md font-bold text-on-surface-medium">
            <Trans>What doesn't change:</Trans>
          </h2>
          <ol className="flex flex-col gap-internal-lg">
            <Change number={1}>
              <Trans>
                You can still invite people, change access, mark exercises complete for others, and
                delete the group.
              </Trans>
            </Change>
            <Change number={2}>
              <Trans>
                Your role in other groups as Member, Leader, or Facilitator stays exactly the same.
              </Trans>
            </Change>
          </ol>
        </section>
        {setRole.isError ? <GroupActionFailed /> : null}
        <div className="flex flex-wrap justify-end gap-content-sm">
          <Button variant="ghost" onClick={goBack}>
            {t`Not now`}
          </Button>
          <Button onClick={accept} loading={setRole.isPending} disabled={!me.data}>
            {t`Accept - Become Facilitator`}
          </Button>
        </div>
      </div>
    </>
  );
}

/** Figma list/line-item: a numbered blue disc and the line beside it. */
function Change({ number, children }: { number: number; children: ReactNode }) {
  return (
    <li className="flex items-start gap-internal-md py-internal-sm">
      {/* The list is ordered: assistive tech already says the number. */}
      <span
        aria-hidden
        className="inline-flex size-7.5 shrink-0 items-center justify-center rounded-full bg-blue-70 font-brand text-caption-sm text-on-action-primary"
      >
        {number}
      </span>
      <span className="flex-1 text-body-sm">{children}</span>
    </li>
  );
}
